import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Wallet, Loader2, Search, Image as ImageIcon } from 'lucide-react';

/**
 * NftPickerDialog
 *
 * Fetches the user's XRPL NFTs (enriched with decoded URI + IPFS metadata)
 * and lets them tap one to auto-fill the host/auction form.
 *
 * Props:
 *  - open, onOpenChange: dialog state
 *  - onSelect(nft): callback with { nft_token_id, name, description, image, collection, issuer }
 *  - hasWallet: boolean — if false, shows "link wallet" CTA instead
 */
export default function NftPickerDialog({ open, onOpenChange, onSelect, hasWallet }) {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !hasWallet) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${API}/xrpl/my-nfts`)
      .then((r) => {
        if (cancelled) return;
        setNfts(r.data?.nfts || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Could not fetch your NFTs');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, hasWallet]);

  const handlePick = (nft) => {
    onSelect?.({
      nft_token_id: nft.nft_token_id,
      name: nft.name || '',
      description: nft.description || '',
      image: nft.image || '',
      collection: nft.collection || '',
      issuer: nft.issuer || '',
    });
    toast.success(`Selected ${nft.name || nft.nft_token_id.slice(0, 10) + '…'}`);
    onOpenChange(false);
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? nfts.filter(n =>
        (n.name || '').toLowerCase().includes(q) ||
        (n.collection || '').toLowerCase().includes(q) ||
        (n.nft_token_id || '').toLowerCase().includes(q))
    : nfts;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nft-picker-dialog" className="bg-slate-900 border-slate-700 max-w-md mx-4 max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-purple-400" /> Pick from your wallet
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Tap an NFT to auto-fill the listing form
          </DialogDescription>
        </DialogHeader>

        {!hasWallet ? (
          <div data-testid="nft-picker-no-wallet" className="text-center py-8">
            <Wallet className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-sm text-slate-300 mb-1">No XRPL wallet linked</p>
            <p className="text-[11px] text-slate-500 mb-4">Link your Xaman / XRPL address in Settings first.</p>
            <Button
              data-testid="nft-picker-link-wallet"
              size="sm"
              variant="outline"
              onClick={() => { onOpenChange(false); window.location.href = '/settings'; }}
              className="border-purple-500/40 text-purple-300"
            >
              Open Settings
            </Button>
          </div>
        ) : loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-6 h-6 mx-auto text-purple-400 animate-spin mb-2" />
            <p className="text-xs text-slate-400">Loading your NFTs…</p>
          </div>
        ) : error ? (
          <div data-testid="nft-picker-error" className="text-center py-8">
            <p className="text-sm text-red-400 mb-1">{error}</p>
          </div>
        ) : nfts.length === 0 ? (
          <div data-testid="nft-picker-empty" className="text-center py-8">
            <ImageIcon className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-sm text-slate-300 mb-1">No NFTs found</p>
            <p className="text-[11px] text-slate-500">
              We didn't find any NFTs in your linked wallet. Make sure your wallet is correct and the NFT is on the active network.
            </p>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                data-testid="nft-picker-search"
                placeholder="Search name / collection / token"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white text-xs h-9 pl-7"
              />
            </div>
            <p className="text-[10px] text-slate-500">
              {filtered.length} of {nfts.length} NFT{nfts.length === 1 ? '' : 's'}
            </p>

            <div className="grid grid-cols-2 gap-2 mt-1">
              {filtered.map((nft) => (
                <button
                  key={nft.nft_token_id}
                  data-testid={`nft-card-${nft.nft_token_id.slice(0, 8)}`}
                  onClick={() => handlePick(nft)}
                  className="text-left bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 hover:border-purple-500/50 rounded-xl overflow-hidden active:scale-[0.98] transition-all"
                >
                  <div className="aspect-square bg-slate-900 flex items-center justify-center">
                    {nft.image ? (
                      <img
                        src={nft.image}
                        alt={nft.name || 'NFT'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-600" />
                    )}
                  </div>
                  <div className="p-2 space-y-0.5">
                    <p className="text-[11px] font-semibold text-white truncate">
                      {nft.name || 'Unnamed NFT'}
                    </p>
                    {nft.collection && (
                      <p className="text-[9px] text-purple-300 truncate">{nft.collection}</p>
                    )}
                    <p className="text-[8px] text-slate-500 font-mono truncate">
                      {nft.nft_token_id.slice(0, 14)}…
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
